import { Check, ExternalLink, Package } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { FeedEvent } from "../../../shared/types";
import { FeedItem } from "../../components/FeedItem";
import { proxiedImageUrl } from "../../lib/imageProxy";
import { navigate } from "../../router";
import { EmptyState, ErrorState, Money, StateLabel } from "../../ui";
import { SkeletonList } from "../../ui/Skeleton";
import { useWs } from "../../ws";

/**
 * Restocks older than this are history, not current work — see Important 1
 * in the final-fix review. This is a cheap client-side pre-filter on top of
 * `recentRestocksStmt` (`src/server/routes.ts`), which has no time window of
 * its own: it's "the last 10 restock events in the 90-day retention window."
 * Generous on purpose — the per-row stock re-check below (`useRestockChecks`)
 * is what actually keeps this section safe to act on; the window just keeps
 * three-month-old rows from being considered at all.
 */
const RESTOCK_WINDOW_MS = 6 * 60 * 60_000; // 6 hours

type BuyJob = {
	id: number;
	productId: number | null;
	qty: number;
	rail: string | null;
	state: string;
	checkoutUrl: string | null;
	debitedMinor: number | null;
	createdAt: number;
};

type NeedsData = {
	jobs: BuyJob[];
	restocks: FeedEvent[];
};

const REFRESH_MS = 30_000;
const REFETCH_DEBOUNCE_MS = 1_000;

async function loadNeeds(): Promise<NeedsData> {
	const [jobsRes, dashRes] = await Promise.all([fetch("/api/buy"), fetch("/api/dashboard")]);
	if (!jobsRes.ok) throw new Error(`HTTP ${jobsRes.status}`);
	if (!dashRes.ok) throw new Error(`HTTP ${dashRes.status}`);
	const jobsJson = (await jobsRes.json()) as { jobs: BuyJob[] };
	// /api/dashboard's recentRestocks is the last 10 `restock` events with no
	// time window and no current-stock check (routes.ts's recentRestocksStmt
	// is a flat `ORDER BY created_at DESC LIMIT 10` over the 90-day retention
	// window) — it is NOT "current restocks". Bound it to a recent window
	// here; the stock re-check that makes the survivors safe to show with a
	// Buy button happens in useRestockChecks, per row.
	const dashJson = (await dashRes.json()) as { recentRestocks: FeedEvent[] };
	const now = Date.now();
	const restocks = dashJson.recentRestocks.filter((e) => now - e.createdAt <= RESTOCK_WINDOW_MS);
	return {
		jobs: jobsJson.jobs.filter((j) => j.state === "awaiting_user"),
		restocks,
	};
}

type RestockCheck = "pending" | "confirmed" | "dropped";

/**
 * Re-verifies current stock for each restock that survived the recency
 * filter, because a restock event is not proof of present stock and this
 * section renders a Buy button. Per-row fetch is acceptable here for the
 * same reason `useProductLite` below is: after the recency filter this is a
 * handful of rows, not a list.
 *
 * Fails closed: no `productId` to check, an HTTP error, or a product that
 * is no longer in stock all resolve to "dropped" rather than "confirmed" —
 * tapping Buy would enqueue a real purchase, so an unconfirmed row must
 * never look actionable. Keyed by the joined id string so an unchanged set
 * of restocks (the common case between polls) doesn't re-fire fetches for
 * rows already resolved.
 */
function useRestockChecks(restocks: FeedEvent[]): Map<number, RestockCheck> {
	const [statuses, setStatuses] = useState<Map<number, RestockCheck>>(new Map());
	const statusesRef = useRef(statuses);
	statusesRef.current = statuses;
	const idsKey = restocks.map((e) => e.id).join(",");

	useEffect(() => {
		let cancelled = false;

		setStatuses((prev) => {
			const next = new Map<number, RestockCheck>();
			for (const e of restocks) {
				next.set(e.id, prev.get(e.id) ?? "pending");
			}
			return next;
		});

		for (const e of restocks) {
			if (statusesRef.current.has(e.id)) continue; // already resolved

			if (e.productId == null) {
				setStatuses((prev) => new Map(prev).set(e.id, "dropped"));
				continue;
			}

			fetch(`/api/products/${e.productId}`)
				.then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
				.then((data: { product?: { inStock?: boolean | null } }) => {
					if (cancelled) return;
					const inStock = data.product?.inStock === true;
					setStatuses((prev) => new Map(prev).set(e.id, inStock ? "confirmed" : "dropped"));
				})
				.catch(() => {
					if (cancelled) return;
					setStatuses((prev) => new Map(prev).set(e.id, "dropped"));
				});
		}

		return () => {
			cancelled = true;
		};
		// idsKey captures the only thing this effect should react to: the set
		// of restock ids. Re-running on every `restocks` array identity change
		// (a new array every poll) would re-fire fetches for rows already
		// resolved.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [idsKey]);

	return statuses;
}

/**
 * The only place `needs` and `live` appear above the fold: buy jobs waiting
 * on the operator's tap, then the restocks that just went buyable. Empty
 * most of the time, which is the point — this is a queue of urgent work,
 * not a feed.
 */
export function NeedsYou() {
	const [data, setData] = useState<NeedsData | null>(null);
	const [error, setError] = useState<string | null>(null);
	const reqIdRef = useRef(0);
	const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const load = useCallback(async () => {
		const reqId = ++reqIdRef.current;
		try {
			const next = await loadNeeds();
			if (reqId !== reqIdRef.current) return;
			setData(next);
			setError(null);
		} catch (e) {
			if (reqId !== reqIdRef.current) return;
			setError((e as Error).message ?? "failed to load");
		}
	}, []);

	useEffect(() => {
		void load();
		const t = setInterval(() => void load(), REFRESH_MS);
		return () => clearInterval(t);
	}, [load]);

	const scheduleRefetch = useCallback(() => {
		if (refetchTimer.current) clearTimeout(refetchTimer.current);
		refetchTimer.current = setTimeout(() => void load(), REFETCH_DEBOUNCE_MS);
	}, [load]);

	useWs((msg) => {
		if (msg.type === "buy.update" || msg.type === "event.new" || msg.type === "sweep.complete") {
			scheduleRefetch();
		}
	});

	// Called unconditionally (before the early returns below) since it's a
	// hook — `data?.restocks ?? []` degrades to "nothing to check" while
	// still loading.
	const restockChecks = useRestockChecks(data?.restocks ?? []);

	if (error && !data) {
		return <ErrorState message={error} onRetry={() => void load()} />;
	}

	if (data == null) {
		return <SkeletonList rows={2} rowHeight={52} />;
	}

	// A restock only counts against "empty" once it's confirmed still in
	// stock, or hasn't been checked yet — a dropped (sold-out, unverifiable,
	// or failed-check) restock is not current work and must not block
	// "Nothing waiting".
	const visibleRestocks = data.restocks.filter((e) => restockChecks.get(e.id) !== "dropped");

	if (data.jobs.length === 0 && visibleRestocks.length === 0) {
		return (
			<EmptyState
				icon={Check}
				title="Nothing waiting"
				description="Restocks and buy jobs that need your tap show up here."
			/>
		);
	}

	return (
		// Foil edge: everything in here is something you can act on right now.
		<ul className="@container divide-y divide-hairline overflow-hidden rounded-[10px] border border-foil">
			{data.jobs.map((job) => (
				<BuyJobRow key={`job-${job.id}`} job={job} />
			))}
			{visibleRestocks.map((e) =>
				restockChecks.get(e.id) === "confirmed" ? (
					<FeedItem key={e.id} event={e} isFresh={false} />
				) : (
					<PendingRestockRow key={e.id} event={e} />
				),
			)}
		</ul>
	);
}

/**
 * Neutral placeholder for a restock whose stock hasn't been re-verified
 * yet: no badge, no Buy button, no colour — this is the "unknown, don't
 * act on it" state, not the "confirmed live" state `FeedItem` renders.
 */
function PendingRestockRow({ event }: { event: FeedEvent }) {
	const p = (event.payload ?? {}) as { title?: string; message?: string };
	const titleText = p.title ?? p.message ?? `event #${event.id}`;
	return (
		<li className="flex items-center gap-3 px-2.5 py-2.5">
			<span className="min-w-0 flex-1 truncate text-[13px] text-muted" title={titleText}>
				{titleText}
			</span>
			<span className="shrink-0 text-xs text-muted">Checking stock…</span>
		</li>
	);
}

type ProductLite = { title: string | null; imageUrl: string | null };

/**
 * Resolves just enough of the product to make a buy-job row legible: the
 * title and, if it has one, a thumbnail. `awaiting_user` jobs are rare
 * (usually zero, occasionally one or two), so a per-row fetch here is fine
 * in a way it never would be for a list — unlike Items/ProductPage, which
 * this task must not touch, this isn't a hot path. Never blocks the row:
 * starts `null` (renders as the id) and stays that way on a slow or failed
 * fetch, since the job still needs the operator's tap either way.
 */
function useProductLite(productId: number | null): ProductLite {
	const [lite, setLite] = useState<ProductLite>({ title: null, imageUrl: null });

	useEffect(() => {
		if (productId == null) {
			setLite({ title: null, imageUrl: null });
			return;
		}
		let cancelled = false;
		setLite({ title: null, imageUrl: null });
		fetch(`/api/products/${productId}`)
			.then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
			.then((data: { product?: { title?: string; imageUrl?: string | null } }) => {
				if (cancelled) return;
				setLite({
					title: data.product?.title ?? null,
					imageUrl: data.product?.imageUrl ?? null,
				});
			})
			.catch(() => {
				// Fall back to the id — see doc comment above.
			});
		return () => {
			cancelled = true;
		};
	}, [productId]);

	return lite;
}

function BuyJobThumbnail({ src, alt }: { src: string | null; alt: string }) {
	const [failed, setFailed] = useState(false);
	const resolved = proxiedImageUrl(src);
	if (!resolved || failed) {
		return (
			<div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-raised text-muted" aria-hidden>
				<Package className="size-4" strokeWidth={1.5} />
			</div>
		);
	}
	return (
		<img
			src={resolved}
			alt={alt}
			loading="lazy"
			onError={() => setFailed(true)}
			className="size-9 shrink-0 rounded-md bg-plate object-contain p-0.5"
		/>
	);
}

function BuyJobRow({ job }: { job: BuyJob }) {
	const { title, imageUrl } = useProductLite(job.productId);
	const fallback = job.productId != null ? `product #${job.productId}` : `job #${job.id}`;

	return (
		<li className="flex flex-wrap items-center gap-2.5 px-2.5 py-2">
			{job.productId != null && <BuyJobThumbnail src={imageUrl} alt={title ?? fallback} />}
			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				<StateLabel state="needs" label="Waiting for your tap" />
				{title ? (
					<span className="min-w-0 truncate text-[13px] font-medium text-ink" title={title}>
						{title}
					</span>
				) : (
					<span className="text-[13px] text-ink">{fallback}</span>
				)}
				<span className="text-xs text-muted">
					qty {job.qty}
					{job.rail ? ` · ${job.rail}` : ""}
					{job.debitedMinor != null && (
						<>
							{" · "}
							<Money minor={job.debitedMinor} />
						</>
					)}
				</span>
			</div>
			<div className="ml-auto flex items-center gap-1.5">
				{job.checkoutUrl && (
					// Foil, like every other control that completes a purchase —
					// hand-rolled because Button doesn't render an <a>.
					<a
						href={job.checkoutUrl}
						target="_blank"
						rel="noreferrer noopener"
						className="inline-flex min-h-tap items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold bg-foil hover:brightness-105 sm:h-7 sm:min-h-0"
					>
						Open checkout <ExternalLink className="size-3.5" aria-hidden />
					</a>
				)}
				<button
					type="button"
					onClick={() => navigate(`/buy/${job.id}`)}
					className="min-h-tap rounded-md border border-hairline bg-paper px-2.5 text-xs font-medium text-ink hover:border-line hover:bg-raised sm:h-7 sm:min-h-0"
				>
					Details
				</button>
			</div>
		</li>
	);
}
